import { useState, useEffect } from 'react';
import AdminSidebar from '../components/AdminSidebar.js';
import { FolderOpen, Upload, RefreshCw, Download, FileText, Trash2, X } from 'lucide-react';
import '../styles/AdminMaterials.css';
import { extractErrorMessage } from '../utils/errorUtils';
import { encodePathSegment } from '../utils/apiPath';

export default function AdminMaterials() {
    const [groups, setGroups] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [selectedSubject, setSelectedSubject] = useState('');
    const [selectedSubjectName, setSelectedSubjectName] = useState('');
    const [groupsLoading, setGroupsLoading] = useState(true);
    const [subjectsLoading, setSubjectsLoading] = useState(false);
    const [files, setFiles] = useState([]);
    const [filesLoading, setFilesLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Fetch groups on component mount
    useEffect(() => {
        async function fetchGroups() {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/courses/getGroups`
                );
                const data = await response.json();
                setGroups(data);
            } catch (error) {
                console.error("Error fetching groups:", error);
            } finally {
                setGroupsLoading(false);
            }
        }
        fetchGroups();
    }, []);

    // Fetch subjects when a group is selected
    useEffect(() => {
        if (selectedGroup) {
            setSubjectsLoading(true);
            setSubjects([]);
            setSelectedSubject('');

            async function fetchSubjects() {
                try {
                    setErrorMsg('');
                    const encodedGroup = encodePathSegment(selectedGroup);
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/courses/groups/${encodedGroup}/modules`
                    );
                    if (!response.ok) {
                        setErrorMsg('Nie udało się pobrać przedmiotów dla tej grupy.');
                        setSubjects([]);
                        return;
                    }
                    const data = await response.json();
                    setSubjects(Array.isArray(data) ? data : []);
                } catch (error) {
                    console.error("Error fetching subjects:", error);
                    setErrorMsg('Nie udało się pobrać przedmiotów dla tej grupy.');
                    setSubjects([]);
                } finally {
                    setSubjectsLoading(false);
                }
            }
            fetchSubjects();
        } else {
            setSubjects([]);
            setSelectedSubject('');
        }
    }, [selectedGroup]);

    const handleGroupChange = (e) => {
        setSelectedGroup(e.target.value);
    };

    const handleSubjectChange = (e) => {
        const subjectId = e.target.value;
        setSelectedSubject(subjectId);
        
        // Find the subject name
        const subject = subjects.find(s => s.module_id.toString() === subjectId);
        if (subject) {
            setSelectedSubjectName(subject.module_name);
        } else {
            setSelectedSubjectName('');
        }
    };

    // Fetch files when a subject is selected
    useEffect(() => {
        if (selectedSubject && selectedGroup) {
            setFilesLoading(true);
            setErrorMsg('');
            
            async function fetchFiles() {
                try {
                    const encodedGroup = encodePathSegment(selectedGroup);
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/materials/admin/${encodedGroup}/${selectedSubject}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                        }
                    );
                    
                    if (!response.ok) {
                        if (response.status === 403) {
                            throw new Error('Brak uprawnień do przeglądania materiałów');
                        }
                        throw new Error('Błąd pobierania plików');
                    }
                    
                    const data = await response.json();
                    setFiles(data.files || []);
                } catch (error) {
                    console.error("Error fetching files:", error);
                    setErrorMsg(extractErrorMessage(error) || 'Błąd pobierania plików');
                    setFiles([]);
                } finally {
                    setFilesLoading(false);
                }
            }
            fetchFiles();
        } else {
            setFiles([]);
        }
    }, [selectedSubject, selectedGroup]);

    const submitUpload = async () => {
        if (!uploadFile || !selectedSubject || !selectedGroup) {
            return;
        }

        setUploading(true);
        setErrorMsg('');
        const formData = new FormData();
        formData.append('file', uploadFile);

        try {
            const encodedGroup = encodePathSegment(selectedGroup);
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/materials/upload/${encodedGroup}/${selectedSubject}`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: formData
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Błąd przesyłania pliku');
            }

            // Refresh files list
            const filesResponse = await fetch(
                `${process.env.REACT_APP_API_URL}/materials/admin/${encodedGroup}/${selectedSubject}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );
            const filesData = await filesResponse.json();
            setFiles(filesData.files || []);
            
            // Close modal and reset
            setUploadOpen(false);
            setUploadFile(null);
        } catch (error) {
            console.error("Error uploading file:", error);
            setErrorMsg(extractErrorMessage(error) || 'Błąd przesyłania pliku');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteFile = async (fileName) => {
        if (!window.confirm(`Czy na pewno chcesz usunąć plik "${fileName}"?`)) {
            return;
        }

        setErrorMsg('');
        try {
            const encodedGroup = encodePathSegment(selectedGroup);
            const encodedFileName = encodeURIComponent(fileName);
            
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/materials/admin/delete/${encodedGroup}/${selectedSubject}/${encodedFileName}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Błąd usuwania pliku');
            }

            // Refresh files list
            const filesResponse = await fetch(
                `${process.env.REACT_APP_API_URL}/materials/admin/${encodedGroup}/${selectedSubject}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );
            const filesData = await filesResponse.json();
            setFiles(filesData.files || []);
        } catch (error) {
            console.error("Error deleting file:", error);
            setErrorMsg(extractErrorMessage(error) || 'Błąd usuwania pliku');
        }
    };

    const handleDownloadFile = (fileName) => {
        const encodedGroup = encodePathSegment(selectedGroup);
        const encodedFileName = encodeURIComponent(fileName);
        const url = `${process.env.REACT_APP_API_URL}/materials/download/${encodedGroup}/${selectedSubject}/${encodedFileName}`;
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const handleRefresh = () => {
        // Reset selections and refetch groups
        setSelectedGroup('');
        setSelectedSubject('');
        setSelectedSubjectName('');
        setSubjects([]);
        setFiles([]);
        setErrorMsg('');
        async function fetchGroups() {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/courses/getGroups`
                );
                const data = await response.json();
                setGroups(data);
            } catch (error) {
                console.error("Error fetching groups:", error);
            }
        }
        fetchGroups();
    };

    return (
        <div className="admin-dashboard-panel admin-materials-panel">
            <AdminSidebar />
            <div className="main-content">
                <div className="admin-materials-inner">
                    {/* HEADER */}
                    <header className="admin-materials-header">
                        <div className="admin-materials-title-wrap">
                            <h1 className="admin-materials-title">
                                <FolderOpen size={20} />
                                Materiały
                            </h1>
                            <p className="admin-materials-subtitle">
                                Zarządzanie materiałami: wybierz grupę i przedmiot.
                            </p>
                        </div>
                        <div className="admin-materials-header-actions">
                            <button
                                className="admin-materials-primary-btn"
                                title="Dodaj plik"
                                onClick={() => {
                                    if (!selectedSubject) {
                                        setErrorMsg('Najpierw wybierz przedmiot');
                                        return;
                                    }
                                    setUploadOpen(true);
                                }}
                                disabled={!selectedSubject}
                            >
                                <Upload size={16} />
                                Dodaj plik
                            </button>
                            <button
                                className="admin-materials-ghost-btn"
                                title="Odśwież"
                                onClick={handleRefresh}
                            >
                                <RefreshCw size={16} />
                            </button>
                        </div>
                    </header>
                    {/* CARD: filters */}
                    <section className="admin-materials-card">
                        <div className="admin-materials-card-head">
                            <div>
                                <h2 className="admin-materials-card-title">Wybór</h2>
                                <p className="admin-materials-card-desc">Grupa → Przedmiot</p>
                            </div>
                            <div className="admin-materials-filters">
                                <label className="admin-materials-label">
                                    <span>Grupa</span>
                                    <select
                                        className="admin-materials-select"
                                        value={selectedGroup}
                                        onChange={handleGroupChange}
                                        disabled={groupsLoading}
                                    >
                                        <option value="">Wybierz grupę</option>
                                        {groups.map((group) => (
                                            <option key={group.group_code} value={group.group_code}>
                                                {group.group_name} ({group.group_code})
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label className="admin-materials-label">
                                    <span>Przedmiot</span>
                                    <select
                                        className="admin-materials-select"
                                        value={selectedSubject}
                                        onChange={handleSubjectChange}
                                        disabled={!selectedGroup || subjectsLoading}
                                    >
                                        <option value="">
                                            {!selectedGroup
                                                ? 'Najpierw wybierz grupę'
                                                : subjectsLoading
                                                    ? 'Ładowanie...'
                                                    : 'Wybierz przedmiot'}
                                        </option>
                                        {subjects.map((subject) => (
                                            <option key={subject.module_id} value={subject.module_id}>
                                                {subject.module_name}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>
                        <div className="admin-materials-card-body">
                            {errorMsg && (
                                <div className="admin-materials-error">
                                    <span>{errorMsg}</span>
                                    <button
                                        onClick={() => setErrorMsg('')}
                                        className="admin-materials-error-close"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            )}
                            {!selectedGroup && !errorMsg && (
                                <p className="admin-materials-empty">
                                    Wybierz grupę, aby zobaczyć przedmioty.
                                </p>
                            )}
                            {selectedGroup && !selectedSubject && !errorMsg && (
                                <p className="admin-materials-empty">
                                    Wybierz przedmiot, aby zobaczyć pliki.
                                </p>
                            )}
                            {selectedSubject && (
                                <div className="admin-materials-files-section">
                                    {filesLoading ? (
                                        <p className="admin-materials-empty">Ładowanie plików...</p>
                                    ) : files.length === 0 ? (
                                        <p className="admin-materials-empty">
                                            Brak plików dla tego przedmiotu.
                                        </p>
                                    ) : (
                                        <div className="admin-materials-grid">
                                            {files.map((file, index) => (
                                                <div key={index} className="admin-materials-file-card">
                                                    <div className="admin-materials-file-icon">
                                                        <FileText size={24} />
                                                        <span className="admin-materials-file-extension">
                                                            {file.name.split('.').pop()?.toUpperCase() || 'FILE'}
                                                        </span>
                                                    </div>
                                                    <div className="admin-materials-file-info">
                                                        <h4 className="admin-materials-file-name" title={file.name}>
                                                            {file.name}
                                                        </h4>
                                                        <p className="admin-materials-file-size">
                                                            {formatFileSize(file.size)}
                                                        </p>
                                                    </div>
                                                    <div className="admin-materials-file-actions">
                                                        <button
                                                            className="admin-materials-file-btn admin-materials-file-download"
                                                            onClick={() => handleDownloadFile(file.name)}
                                                            title="Pobierz plik"
                                                        >
                                                            <Download size={16} />
                                                        </button>
                                                        <button
                                                            className="admin-materials-file-btn admin-materials-file-delete"
                                                            onClick={() => handleDeleteFile(file.name)}
                                                            title="Usuń plik"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </div>
            {/* Upload modal */}
            {uploadOpen && (
                <div className="admin-materials-modal-overlay" onMouseDown={() => setUploadOpen(false)}>
                    <div className="admin-materials-modal" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="admin-materials-modal-head">
                            <div>
                                <h3 className="admin-materials-modal-title">Dodaj plik</h3>
                                <p className="admin-materials-modal-subtitle">
                                    Przedmiot: <b>{selectedSubjectName}</b>
                                </p>
                            </div>

                            <button
                                className="admin-materials-modal-close"
                                onClick={() => {
                                    setUploadOpen(false);
                                    setErrorMsg('');
                                    setUploadFile(null);
                                }}
                                title="Zamknij"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        <div className="admin-materials-modal-body">
                            <label className="admin-materials-upload">
                                <input
                                    type="file"
                                    onChange={(e) => {
                                        setUploadFile(e.target.files?.[0] || null);
                                        setErrorMsg(''); // Clear error when new file selected
                                    }}
                                />
                                <span>
                                    {uploadFile ? uploadFile.name : "Wybierz plik…"}
                                </span>
                            </label>
                            <p className="admin-materials-modal-subtitle">Maksymalny rozmiar pliku: 100 MB</p>

                            {errorMsg && (
                                <div style={{ 
                                    marginTop: '1rem', 
                                    padding: '0.75rem', 
                                    backgroundColor: '#fee', 
                                    color: '#c33', 
                                    borderRadius: '4px',
                                    fontSize: '0.875rem'
                                }}>
                                    {errorMsg}
                                </div>
                            )}

                            <div className="admin-materials-modal-actions">
                                <button
                                    className="admin-materials-btn"
                                    onClick={() => {
                                        setUploadOpen(false);
                                        setErrorMsg('');
                                        setUploadFile(null);
                                    }}
                                    disabled={uploading}
                                >
                                    Anuluj
                                </button>
                                <button
                                    className="admin-materials-primary-btn"
                                    onClick={submitUpload}
                                    disabled={!uploadFile || uploading}
                                >
                                    <Upload size={16} />
                                    {uploading ? "Wgrywanie..." : "Dodaj"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}